
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, Container, Modal } from "react-bootstrap";
import { ArrowDown, ArrowUp, Check, CheckCircle, ChevronDown, ChevronUp, Eye, FileText, FileUp, Info, Lock, MessageSquareText, PackageCheck, Pencil, RotateCcw, Save, ScanLine, X } from "lucide-react";
import { useAuth } from "../../../../contexts/AuthContext";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { CastType, IQuery, IQuerySelectData, IWhere } from "../../../../component/solution/Interfaces";
import { StockTake } from "../../../../component/retail/StockTake";
import { StoreReturn } from "../../../../component/retail/StoreReturn";
import { IBTManagement } from "../../../../component/retail/IBTManagement";

interface IStore {
    id: string;
    code: string;
    name: string;
    email: string
    customer_code: string;
    costing_code: string;
    sit_location: string;
    shopify_location_id: string;
    created: string;
    updated: string| null;

    // Optional new fields
    address?: string;
    stock_receipt_count?: number;
    stock_receipt_min_created?: string | null;
    stock_take_count?: number;
    stock_take_min_created?: string | null;
    cycle_count_count?: number;
    cycle_count_min_created?: string | null;
    stock_return_count?: number;
    stock_return_min_created?: string | null;
    stock_transfer_count?: number;
    stock_transfer_min_created?: string | null;
    internal_stock_transfer_count?: number;
    internal_stock_transfer_min_created?: string | null;
    send_stock_transfer_count?: number;
    send_stock_transfer_min_created?: string | null;
    stock_receive_count?: number;
    stock_receive_min_created?: string | null;
    dispatched_count?: number;
    dispatched_min_created?: string | null;
}



interface IStockMovementOut {
    id: string;
    doc_num: number;
    doc_entry: number;
    card_code: string | null;
    card_name: string | null;
    address: string | null;
    phone: string | null;
    from_warehouse: string | null;
    to_warehouse: string | null;
    integration_status: string;
    created: string;
    updated: string;
    completed_at: string | null; 
    function_call_id: string | null; 
    handled_status: string | null; 
    extended_message: string | null; 
}

interface IStockMovementOutLine {
    id: string;
    doc_num: number;
    line_num: number;
    item_code: string;
    "item.style": string;
    qty_ordered: number;
    from_warehouse: string | null;
    to_warehouse: string | null;
    qty_sent: number | null;
    qty_received: number| null;
}

interface IStoreReceipt {
    id: string;
    created: string;
    updated: string;
    doc_num: number;
    receipt_code: string;
    notes: string;
    status: string
    total_qty: string;
}

interface IItem {
    standard_unit_value: number;
    item_code: string;
    integration_status: string;
    colour: string;
    size: string;
    image_url: string | null;
    item_type: string | null;
    product_control_type_code: string;
    name: string;
    style: string;
    product_code: string;
    barcode: string;
}

interface ILine {
    line_num: number;
    item_code: string;
    item: IItem;
    qty: number;
}

interface IStoreReceiptLines {
    id: string;
    created: string;
    updated: string;
    doc_num: number;
    receipt_code: string;
    notes: string;
    status: string
    total_qty: string;
    lines: ILine[];
}

interface IComponent {
    store: IStore, 
    close: () => void,
    isCycleCount?: boolean; // Optional boolean prop
    allStores?: IStore[];
}

interface ShowErrorAlertProps {
    message: string | null;
}

interface ISelectedScannedItem {
  id: string;
  barcode: string;
  item_code: string;
  item_group: string;
  name: string;
  product_code: string;
}

//This is the component design for the error alert [Testing Phase]
const ShowErrorAlert: React.FC<ShowErrorAlertProps>  = ({ message }) => {
    const { handleCloseErrorAlert }  = useAuth();

   /* if(!message){
        return null; 
    } */

    return (
    <div id="error-alert" role="alert" style={{ display: 'flex', alignItems: 'center', padding: '10px', marginBottom: '2px', backgroundColor: '#fef2f2', color: '#e53e3e', borderRadius: '8px', border: '1px solid #e53e3e' }}>
        <svg style={{ flexShrink: 0, width: '16px', height: '16px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" >
            <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
        </svg>
        <div style={{ marginLeft: '12px', fontSize: '14px', fontWeight: '500' }}>
         { message }
        </div>
        <button type="button" style={{ marginLeft: 'auto', marginTop: '-6px', marginBottom: '-6px', backgroundColor: '#fef2f2', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
          aria-label="Close"
          onClick={ () => handleCloseErrorAlert()}
        >
        <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
            <path stroke="#e53e3e" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
        </svg>
        </button>
    </div>
    );
}

// const CycleCount: React.FC<IComponent | null> = (props) => {

//     console.log(props?.isCycleCount);
//     return (
//         <>
//             <div>
//                 <button
//                     className="btn btn-secondary btn-sm flex-grow-1"
//                     style={{width: "120px"}}
//                     onClick={() => props?.close()}
//                 >
//                     CycleCount - Back
//                 </button>
//             </div>
//         </>
//     );
// }

// will need to put in the table here. 
// const StockTake: React.FC<IComponent | null> = (props) => {
//     return (
//         <>
//             <div>
//                 <button
//                     className="btn btn-secondary btn-sm flex-grow-1"
//                     style={{width: "120px"}}
//                     onClick={() => props?.close()}
//                 >
//                     StockTake - Back
//                 </button>
//             </div>
            
//         </>
//     );
// }

const StoreReceipt :React.FC<{setHasPostedReciepts: (value: boolean) => void, hasPostedReciepts: boolean, stockMovmentOut: IStockMovementOut, close: () => void}> = (props) => {

    const {fetchData, userDetail, responseErrorMessage, showErrorAlert }  = useAuth();
    const fetchReceiptsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RECEIPT-ACTIONS/run?doc_num=${props.stockMovmentOut.doc_num}`;
    const fetchSelectedReceiptLinesQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RECEIPT-ACTIONS/run?doc_num=${props.stockMovmentOut.doc_num}`;
    const fetchSelectedReceiptLinesActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RECEIPT-LINE-ACTIONS/run`; 
    const fetchSelectedReceiptActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STORE-RECEIPT-ACTIONS/run`; 
    const fetchSelectedReceiptMovementOutActionsQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/functions/STOCK-MOVEMENT-OUT-ACTIONS/run`;
    const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;

    const [createNoteState, setCreateNoteState] = useState<string>("");
    const [storeReceiptsState, setStoreReceiptsState] = useState<IStoreReceipt[]>([]);
    const [selectedStoreReceiptState, setSelectedStoreReceiptState] = useState<IStoreReceipt | null>(null);
    const [selectedStoreReceiptStateValid, setSelectedStoreReceiptStateValid] = useState<IStoreReceipt | null>(null);
    const [selectedStoreReceiptLinesState, setSelectedStoreReceiptLinesState] = useState<IStoreReceiptLines[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [viewMode, setViewMode] = useState<'file' | 'scan' | 'view' | 'default'>('default'); 
    const [loading, setLoading] = useState<boolean>(false);

    const [showUpdateStatusStoreReceiptModal, setShowUpdateStatusStoreReceiptModal] = useState<boolean>(false);
    const [showDeleteStoreReceiptModal, setShowDeleteStoreReceiptModal] = useState<boolean>(false);
    const [shouldResetAfterClose, setShouldResetAfterClose] = useState<boolean>(false);
    const [selectedDocNum, setSelectedDocNum] = useState<string>("");
    const [selectedReceiptCode, setSelectedReceiptCode] = useState<string>("");


    const [selectedScannedItem, setSelectedScannedItem] = useState<ISelectedScannedItem | null>(null);  
    const [showCustomQuantitySection, setShowCustomQuantitySection] = useState<boolean>(false);
    const [customQuantity, setCustomQuantity] = useState<string>('1');

    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [editedQty, setEditedQty] = useState<Record<string, number>>({}); 
    const [inputDraft, setInputDraft] = useState<Record<string, string>>({});

    const defaultQueryState: IQuerySelectData = {
        appCode: "ares",
        selectCode: "scan-item",  
        params: {}
    };   
            
    const [querySelectState, setQuerySelectState] = useState<IQuerySelectData>(defaultQueryState);


    //this sets the loading of to false if theres an error message
    useEffect(() => {
        if(showErrorAlert){
           setLoading(false);
        }else{
           setLoading(false);
        } 
        //setLoading(false);
    }, [showErrorAlert])


    useEffect(() => {
        setLoading(true); 
        fetchData(fetchReceiptsQueryUrl, {method:"GET"}, processListReceiptsData);
    }, []);

    useEffect(() => {
        fetchData(fetchSelectedReceiptLinesQueryUrl, {method:"GET"}, processListReceiptLineData);
        //console.log(fetchSelectedReceiptLinesQueryUrl);
    }, [selectedStoreReceiptState]);

      useEffect(() => {
        fetchData(fetchSelectedReceiptLinesQueryUrl, {method:"GET"}, processListReceiptLineData);
        //console.log(fetchSelectedReceiptLinesQueryUrl);
    }, [selectedStoreReceiptStateValid]);

    const scanRef = useRef<HTMLInputElement | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (selectedStoreReceiptState && scanRef.current) {
           scanRef.current.focus();
        }
        if (selectedStoreReceiptState && fileRef.current) {
            fileRef.current.focus();
         }
    }, [selectedStoreReceiptState]);


    //These useeffects work in conjuction with one another ensuring the selectedStoreReceiptState.receipt_code is available. 
    useEffect(() => {
        setLoading(true);  // Set loading state before fetching
        if (selectedStoreReceiptState?.receipt_code) {
            const receiptCode = selectedStoreReceiptState.receipt_code;
            fetchData(`${fetchSelectedReceiptLinesActionsQueryUrl}?receipt_code=${receiptCode}`, { method: "GET" }, processListReceiptLineData);
        }
    }, [selectedStoreReceiptState]); // This will trigger only when selectedStoreReceiptState is set
    
    useEffect(() => {
        if (selectedStoreReceiptState && selectedStoreReceiptState.receipt_code) {
            // If selectedStoreReceiptState is set and has a receipt_code, fetch the data.
            const receiptCode = selectedStoreReceiptState.receipt_code;
            fetchData(`${fetchSelectedReceiptLinesActionsQueryUrl}?receipt_code=${receiptCode}`, { method: "GET" }, processListReceiptLineData);
        }
    }, [selectedStoreReceiptState?.receipt_code]); 

     useEffect(() => {
        setLoading(true);  // Set loading state before fetching
        if (selectedStoreReceiptStateValid?.receipt_code) {
            const receiptCode = selectedStoreReceiptStateValid.receipt_code;
            fetchData(`${fetchSelectedReceiptLinesActionsQueryUrl}?receipt_code=${receiptCode}`, { method: "GET" }, processListReceiptLineData);
        }
    }, [selectedStoreReceiptStateValid]); // This will trigger only when selectedStoreReceiptState is set
    
    useEffect(() => {
        if (selectedStoreReceiptStateValid && selectedStoreReceiptStateValid.receipt_code) {
            // If selectedStoreReceiptState is set and has a receipt_code, fetch the data.
            const receiptCode = selectedStoreReceiptStateValid.receipt_code;
            fetchData(`${fetchSelectedReceiptLinesActionsQueryUrl}?receipt_code=${receiptCode}`, { method: "GET" }, processListReceiptLineData);
        }
    }, [selectedStoreReceiptStateValid?.receipt_code]); 

   

    const processListReceiptsData = (result: any) => {
        setLoading(false); 

        const storeReceipts: IStoreReceipt[] = [];
        setCreateNoteState("");

        for(const key in result){
            storeReceipts.push({...result[key].data, ...result[key].control});
            //console.log({...result[key].data, ...result[key].control});
        }
        
        //sorting through the list by created which is the date of when the receipt was made.
        storeReceipts.sort((a, b) => {
            if (a.created < b.created) return -1;
            if (a.created > b.created) return 1;
            return 0;
        });

        setStoreReceiptsState(storeReceipts);
    }


    const processListReceiptLineData = (result: any) => {


        setLoading(false);
        const receiptLines: IStoreReceiptLines[] = [];
        setCreateNoteState("");

        result.forEach((receiptWrapper: any) => {
            const receipt = receiptWrapper.data;

            // Check if valid line information is present
            const hasLineInfo = receipt["lines.line_num"] !== undefined && (receipt["lines.item_code"] || receipt["lines.item.item_code"]);

            if (!hasLineInfo) {
            // console.warn("Skipping receipt due to missing line info:", receipt);
              return;
            }

            const line: ILine = {
                line_num: receipt["lines.line_num"],
                item_code: receipt["lines.item_code"] || receipt["lines.item.item_code"],
                qty: receipt["lines.qty"],
                item: {
                    standard_unit_value: receipt["lines.item.standard_unit_value"],
                    item_code: receipt["lines.item.item_code"],
                    integration_status: receipt["lines.item.integration_status"],
                    colour: receipt["lines.item.colour"],
                    size: receipt["lines.item.size"],
                    image_url: receipt["lines.item.image_url"],
                    item_type: receipt["lines.item.item_type"],
                    product_control_type_code: receipt["lines.item.product_control_type_code"],
                    name: receipt["lines.item.name"],
                    style: receipt["lines.item.style"],
                    product_code: receipt["lines.item.product_code"],
                    barcode: receipt["lines.item.barcode"]
                }
            };

            const receiptLine: IStoreReceiptLines = {
                id: receipt.receipt_code,
                created: receipt.created || "",
                updated: receipt.updated || "",
                doc_num: receipt.doc_num,
                receipt_code: receipt.receipt_code,
                notes: receipt.notes,
                status: receipt.status,
                total_qty: receipt.total_qty,
                lines: [line]
            };

            // console.log(`=======Testing what the receipt line looks like =======`);
            // console.log(receiptLine);
            // console.log(`=====================`);

            receiptLines.push(receiptLine);
        });

        setSelectedStoreReceiptLinesState(receiptLines);
    };

    

    const handleCreateReceipt = () => {
        if(createNoteState !== ""){
            setLoading(true); 
            const extendedNotes: string = `${createNoteState} - ${userDetail?.userName} [${userDetail?.firstName} ${userDetail?.lastName}]`;
            fetchData(fetchReceiptsQueryUrl, {method:"POST", body: JSON.stringify({ doc_num: props.stockMovmentOut.doc_num, notes: extendedNotes})}, processCreateReceiptData);
        }else{
            return; 
        }
    }

    //handle the posting of a receipt [POSTING]
    const handleRecieptPostSubmit = () => {
        fetchData(fetchSelectedReceiptMovementOutActionsQueryUrl, {
            method: "POST",  
            body: JSON.stringify({ doc_num: props.stockMovmentOut.doc_num, integration_status: "TO_CLOSE" })
        }, (result: any) => { 
            //This indicates theres nothing wrong with the request
            if (!showErrorAlert) {
                //This sends the user back to the Receive Stock view and changing the state will cause a render on that component
                props.setHasPostedReciepts(!props.hasPostedReciepts);
                props.close(); 
            }else{
                processCreateReceiptData(result);
            } 
          });
    }



    //handle open modal of closing a store reciept
    const handleRecieptCloseModal = (storeReceipt : IStoreReceipt) => {
        setViewMode('default'); // Switch to default mode
        setShowUpdateStatusStoreReceiptModal(true);
        setSelectedStoreReceiptStateValid(storeReceipt);
        setSelectedDocNum(String(storeReceipt.doc_num));
    }

    //handle the closing of a receipt [CLOSE]
    const handleRecieptCloseSubmit = () => {
 
         const isValid = selectedDocNum !== "" && selectedStoreReceiptLinesState.length > 0;

            if (!isValid) return;

            const notes: string = `${createNoteState} - ${userDetail?.userName} [${userDetail?.firstName} ${userDetail?.lastName}]`; 
            setLoading(true);

            const resetState = () => { setSelectedDocNum(""); setSelectedStoreReceiptStateValid(null); setSelectedStoreReceiptLinesState([]); };

            fetchData(`${fetchSelectedReceiptActionsQueryUrl}?receipt_code=${selectedStoreReceiptStateValid?.receipt_code}`, { 
                method: "PUT", 
                body: JSON.stringify({ notes: notes, status: "CLOSED" }) 
            }, (result: any, status: string) => {
                 resetState();
                if (status === "200") {
                   processCreateReceiptData(result);
                }
            });
    };

   //handle the open modal for deleting of a receipt [DELETE]
   const handleRecieptDeleteModal = (storeReceipt : IStoreReceipt) => {
        setViewMode('default'); // Switch to default mode
        setShowDeleteStoreReceiptModal(true);
        setSelectedReceiptCode(storeReceipt.receipt_code);
   }

    //handle the deleting of a receipt [DELETE]
    const handleRecieptDeleteSubmit = () => {
        setLoading(true); 
        if(selectedReceiptCode){
            fetchData(`${fetchSelectedReceiptActionsQueryUrl}?receipt_code=${selectedReceiptCode}`, {
                method: "DELETE"
            }, processCreateReceiptData);
            setSelectedReceiptCode("");
        }else{
            return;
        }
    }


    const processCreateReceiptData = (result: any) => {
        fetchData(fetchReceiptsQueryUrl, {method:"GET"}, processListReceiptsData);
        setLoading(false); 
    }

    //handle view receipt when clicked
    const handleViewRecieptClick = (storeReceipt : IStoreReceipt) => {
        setViewMode('view'); // Switch to view mode
        setSelectedStoreReceiptState(storeReceipt);
    }
    
    //handle scan button when clicked
    const handleScanClick = (storeReceipt : IStoreReceipt) => {
        setViewMode('scan'); // Switch to Scan mode
        setSelectedStoreReceiptState(storeReceipt);
    }

    //handle file button when clicked
    const handleFileClick = (storeReceipt : IStoreReceipt) => {
        setViewMode('file');  // Switch to File mode
        setSelectedStoreReceiptState(storeReceipt);
    };


    // Add the event handler for "Enter" key press
    const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, receiptCode: string) => {
        if (event.key === 'Enter') {
            event.preventDefault();  // Prevent default behavior (if needed)
            handleScanSubmit(receiptCode); 
        }
    };

    // Handle file/folder selection
    const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]; // Select the first file (if any)
        if (file) {
            setSelectedFile(file); // Set the selected file
        } else {
            setSelectedFile(null); // Handle case where no file is selected
        }
    };


    const handleScanSubmit = (receiptCode: string) => {
            setLoading(true);

            const scanValueItemCode = scanRef.current?.value.trim();
            if (!receiptCode || !scanValueItemCode) {
                setLoading(false);
                return;
            }

            const queryPayload = {
                ...querySelectState,
                params: { ...querySelectState.params, scan_code: scanValueItemCode },
            };

            let shouldRunLineFetch = true;

            // Step 1: Fetch item metadata (to check item group, etc.)
            fetchData(
                queryUrlSelectNoCount,
                { method: "POST", body: JSON.stringify(queryPayload) },
                (result: any, status: string) => {
                    if (status === "200") {
                        const item = result?.data?.[0]?.data;
                        const id = result?.data?.[0]?.id;

                        if (item && id) {
                            const itemGroup = item.item_group;

                            const scannedItem: ISelectedScannedItem = {
                                id,
                                barcode: item.barcode,
                                item_code: item.item_code,
                                item_group: itemGroup,
                                name: item.name,
                                product_code: item.product_code,
                            };

                            setSelectedScannedItem(scannedItem);

                            if (itemGroup === "Jibbitz") {
                                setShowCustomQuantitySection(true);
                                shouldRunLineFetch = false; // Skip fetch for Jibbitz
                            }
                        }
                    }

                    // Step 2: Conditionally fetch receipt line data
                    if (shouldRunLineFetch) {
                        fetchData(fetchSelectedReceiptLinesActionsQueryUrl,
                            { method:"POST", body: JSON.stringify({ receipt_code: receiptCode, item_code: scanValueItemCode})}, 
                            (result: any, status: string) => { 
                                //This indicates theres nothing wrong with the request
                                if (status === "200") { 
                            
                                    // Only update the status to "SCANNING" if it's on "OPEN" for scanning codes
                                    if (selectedStoreReceiptState && selectedStoreReceiptState.status === "OPEN") {
                                        const shallowCopy: IStoreReceipt = {
                                        ...selectedStoreReceiptState,
                                        status: "SCANNING"
                                        };
                                        setSelectedStoreReceiptState(shallowCopy);
                                    } 

                                    processRecieptLineActionData(result, receiptCode)
                                }
                        }); 
                    } else {
                        setLoading(false); // Clean up if skipping line fetch
                    }
                }
            );

            if (scanRef.current) {
                scanRef.current.value = "";
            }
    };


    const handleQuantitySubmit = (receipt_code: string) => {

       if (!selectedScannedItem || !receipt_code) return;

       const { item_code, barcode } = selectedScannedItem;
       const code = item_code?.trim() || barcode?.trim();

       const parsedQty = Math.max(1, parseInt(customQuantity) || 1);

       const payload = {
        receipt_code: receipt_code,
        item_code: code,
        qty: parsedQty
       };

       setLoading(true);

       fetchData(fetchSelectedReceiptLinesActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) => { 
            //This indicates theres nothing wrong with the request
            if (status === "200") { 
        
                // Only update the status to "SCANNING" if it's on "OPEN" for scanning codes
                if (selectedStoreReceiptState && selectedStoreReceiptState.status === "OPEN") {
                    const shallowCopy: IStoreReceipt = {
                    ...selectedStoreReceiptState,
                    status: "SCANNING"
                    };
                    setSelectedStoreReceiptState(shallowCopy);
                } 

                setShowCustomQuantitySection(false);
                setSelectedScannedItem(null);
                setCustomQuantity('1');

                processRecieptLineActionData(result, receipt_code)
            }
       }); 
    };

    const handleFileSubmit = (receiptCode: string) => {
         setLoading(true); 
        if (selectedFile) {
            // how to do a multipe part form posts javascript  
            const formData = new FormData();
            formData.append('f', selectedFile);
            formData.append('j1', new Blob([JSON.stringify({ receipt_code: receiptCode })], { type: 'application/json' }));

            const headers = new Headers();
            headers.append("Content-Type", "multipart/formdata");

            fetchData(fetchSelectedReceiptLinesActionsQueryUrl, 
                { method:"POST", body: formData, headers: headers }, 
                ((result: any, status: string) => { 
                    
                    //This indicates theres nothing wrong with the request
                    if (status === "200") { 
                    
                        // Only update the status to "CLOSED" if it's on "OPEN" for fole upload
                        if (selectedStoreReceiptState && selectedStoreReceiptState.status === "OPEN") {
                            const shallowCopy: IStoreReceipt = {
                            ...selectedStoreReceiptState,
                            status: "CLOSED"
                            };
                            setSelectedStoreReceiptState(shallowCopy);
                        } 
                    } 
                    processRecieptLineActionData(result, receiptCode) 
                
                } )); 

            setViewMode('view');//sets to view mode to hide file input when  on file mode. 

            // Reset fileRef after submission
            setSelectedFile(null);
            if (fileRef.current) {
                fileRef.current.value = ''; 
            }
        }else{
            return;
        }
    };

    const processRecieptLineActionData = (result: any, receiptCode: string) => {
        setLoading(false);  // Data has been loaded, hide the loader   
        fetchData(`${fetchSelectedReceiptLinesActionsQueryUrl}?receipt_code=${receiptCode}`, { method:"GET" }, processListReceiptLineData);
    } 


    const handleEditClick = (id: string, initialQty: number) => {
      setEditingRowId(id);
      setEditedQty(prev => ({ ...prev, [id]: initialQty }));
      setInputDraft(prev => ({ ...prev, [id]: initialQty.toString() }));
    };

    const handleQtyDraftChange = (id: string, value: string) => {
         setInputDraft(prev => ({ ...prev, [id]: value }));
    };


    const handleBlur = (id: string) => {
        const draftValue = inputDraft[id];
        const parsed = parseInt(draftValue, 10);
        setEditedQty(prev => ({ ...prev, [id]: isNaN(parsed) ? 0 : parsed }));
    };

    const handleSaveClick = (id: string, receipt_code: string, item_code: string) => {
        
        const qty = inputDraft[id];

        if (!receipt_code || qty === undefined || item_code === undefined) return;

        const payload = {
            receipt_code,
            item_code, 
            qty: qty,
            mode: 'exact'
        };

        // Perform your save operation here (e.g., API call)
        fetchData(fetchSelectedReceiptLinesActionsQueryUrl,
        { method:"POST", body: JSON.stringify(payload)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
                processRecieptLineActionData(result, receipt_code);
                setEditingRowId(null); // exit edit mode
                setEditedQty({});
                setInputDraft({});
            }
        });
    };

    // Function to check if all receipts have status 'CLOSED' and 'DELETED'
    const areAllReceiptsClosed = storeReceiptsState.length === 0 ? false : storeReceiptsState.every(receipt => receipt.status === 'CLOSED' || receipt.status === 'DELETED');
     
    //handles the back to reciepts reloading the list.
    const handleBackToReceipts = () => {
        setLoading(true);
        setViewMode('default');
        setSelectedStoreReceiptState(null);
        setShowCustomQuantitySection(false);
        setSelectedScannedItem(null);
        setCustomQuantity('1');
        setEditingRowId(null); // exit edit mode
        setEditedQty({});
        setInputDraft({});
        fetchData(fetchReceiptsQueryUrl, {method:"GET"}, processListReceiptsData);
    }

    const handleCloseForm = () => {
      setSelectedScannedItem(null);
      setCustomQuantity('1');
      setShowCustomQuantitySection(false); 
    };


    const DisabledFileBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Upload File"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
             >
             <FileUp style={{ width: "17px", height: "17px" , color: "#6c757d" }} />
           </span>
        ) 
    } 
   

    const DisabledScanBtn = () => {
        return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Scan"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
             >
             <ScanLine style={{ width: "17px", height: "17px" , color: "#6c757d" }} />
           </span>
        ) 
    } 

    const DisabledCheckBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Close Store Receipt"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <Check style={{ width: "17px", height: "17px" , color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledDeleteBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Remove Store Reciept"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <X style={{ width: "17px", height: "17px" , color: "#6c757d" }} />
        </span>
       )
    } 

    const DisabledEditQtyBtn = () => {
            return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Edit Quantity"
                style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
            >
            <Pencil style={{ width: "17px", height: "17px" , color: "#6c757d" }} />
            </span>
        )
    } 

    const DisabledSaveQtyBtn = () => {
            return (
            <span className="btn h-100 d-flex align-items-center gap-1" title="Save Quantity"
                    style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
                >
                <Save style={{ width: "17px", height: "17px", color: "#6c757d" }} />
            </span>
            )
    } 
  
    
    const thStyle = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const tdStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };



    return (
        <> 
            { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
            <Card>
                <Card.Header>
                    <Card.Title>{`Store Receipts - [Receiving for Document: ${props.stockMovmentOut.doc_num}]`}</Card.Title>                       
                </Card.Header>
                <CardBody>
                    <div className="d-flex gap-1">
                        <button
                            className="btn btn-secondary h-100"
                            style={{width: viewMode === 'default' ? "120px" : "140px"}}
                            onClick={() => {
                                if (viewMode === 'default') {
                                    props.close(); 
                                } else {
                                    handleBackToReceipts();
                                }
                            }}
                          >
                            { viewMode === 'default' ? "Back to Store" : "Back To Receipts" }
                        </button>
                        <button
                            className="btn btn-info h-100"
                            style={{width: "120px", display: areAllReceiptsClosed && viewMode === 'default' && props.stockMovmentOut.integration_status === 'DISPATCHED' ? 'block' : 'none' }}
                            onClick={() => {handleRecieptPostSubmit()}}
                        >
                            Post
                        </button>  
                        <button  
                            className="btn btn-success h-100"
                            style={{width: "120px", display: viewMode === 'default' && props.stockMovmentOut.integration_status === 'DISPATCHED' ? 'block' : 'none' }}
                            onClick={() => {handleCreateReceipt()}}
                            disabled={createNoteState === ""}
                        >
                            Create Receipt
                        </button> 
                        <input 
                            id="shipment"
                            type="text"
                            value={createNoteState}
                            onChange={(e) => setCreateNoteState(e.target.value)}
                            placeholder="Description for new receipt ..."
                            className="form-control form-control-md mb-3 w-25"
                            autoComplete="off"
                            style={{ display: viewMode === 'default' && props.stockMovmentOut.integration_status === 'DISPATCHED' ? 'block' : 'none' }}
                        />                                             
                    </div>                 
                    <br></br>                                       
                    <div>
                        <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                        <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle} >Code</th>
                                    <th style={thStyle}>Notes</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Updated</th>
                                    { props.stockMovmentOut.integration_status === 'DISPATCHED' && <th style={thStyle}>Action</th>}
                                </tr>
                            </thead>
                            <tbody> 

                            {loading && viewMode === "default" ? (
                           
                                <tr>
                                    <td colSpan={props.stockMovmentOut.integration_status === 'DISPATCHED' ? 6 : 5} className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            
                            ) : (
                                <>
                                {!selectedStoreReceiptState && viewMode === "default" && storeReceiptsState.map((storeReceipt: IStoreReceipt) => (
                                    <tr key={storeReceipt.id}>
                                        <td 
                                         className="text-center"
                                         style={{...tdStyle, cursor: 'pointer', textDecoration: 'underline'}}
                                         onClick={() => handleViewRecieptClick(storeReceipt)}
                                         >
                                            <b>{storeReceipt.receipt_code}</b>
                                        </td>
                                        <td style={tdStyle}>{storeReceipt.notes}</td>
                                        <td style={tdStyle}>{storeReceipt.status}</td>
                                        <td style={tdStyle}>{formatDateFull(storeReceipt.created)}</td>
                                        <td style={tdStyle}>{formatDateFull(storeReceipt.updated)}</td> 
                                        { props.stockMovmentOut.integration_status === 'DISPATCHED' && 
                                        <td style={tdStyle} className="d-flex gap-1">

                                            {(storeReceipt.status === "CLOSED" || storeReceipt.status === "SCANNING" || storeReceipt.status === "DELETED") ? (
                                                <DisabledFileBtn/>
                                                ) : (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Upload File'
                                                    onClick={() => handleFileClick(storeReceipt)}
                                                >
                                                 <FileUp style={{ width: "17px", height: "17px" }}  /> 
                                                </button>
                                            )} 

                                             {(storeReceipt.status === "CLOSED" || storeReceipt.status === "DELETED") ? (
                                                <DisabledScanBtn/>
                                                ) : (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Scan'
                                                    onClick={() => handleScanClick(storeReceipt)}
                                                >
                                                 <ScanLine style={{ width: "17px", height: "17px" }}  /> 
                                                </button>
                                            )} 
    
    
                                             {(storeReceipt.status === "CLOSED" || storeReceipt.status === "DELETED") ? (
                                                <DisabledCheckBtn/>
                                                ) : (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Close Store Receipt'
                                                    onClick={() => handleRecieptCloseModal(storeReceipt)}
                                                >
                                                 <Check style={{ width: "17px", height: "17px" }}  /> 
                                                </button>
                                             )} 
                           

                                              { storeReceipt.status === "DELETED" ? (
                                                 <DisabledDeleteBtn/>
                                                ) : (
                                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title='Remove Store Reciept'
                                                    onClick={() => handleRecieptDeleteModal(storeReceipt)}
                                                >
                                                    <X style={{ width: "17px", height: "17px" }}  /> 
                                                </button>
                                             )}              
                                        </td>
                                        } 
                                    </tr>
                                ))}  
                                </>
                             )} 


                                {selectedStoreReceiptState && viewMode !== "default" &&
                                    <>
                                        <tr key={selectedStoreReceiptState.id}>
                                            <td style={tdStyle} className="text-center"><b>{selectedStoreReceiptState.receipt_code}</b></td>
                                            <td style={tdStyle}>{selectedStoreReceiptState.notes}</td>
                                            <td style={tdStyle}>{selectedStoreReceiptState.status}</td>
                                            <td style={tdStyle}>{formatDateFull(selectedStoreReceiptState.created)}</td>
                                            <td style={tdStyle}>{formatDateFull(selectedStoreReceiptState.updated)}</td> 
                                        </tr>
                                        <tr>
                                        <td colSpan={6} style={{ textAlign: 'center' }}>

                                             {viewMode === 'view' ? null : (
                                                <>
                                                    {/* Quantity Form (only in scan mode and when showing scanned item details) */}
                                                    {viewMode === 'scan' && selectedScannedItem && showCustomQuantitySection ? (
                                                    <form
                                                        onSubmit={(e) => {
                                                            e.preventDefault();
                                                            handleQuantitySubmit(selectedStoreReceiptState.receipt_code);
                                                        }}
                                                        className="p-3 border rounded bg-light"
                                                        style={{ display: 'inline-block', textAlign: 'left', minWidth: '600px' }}
                                                    >
                                                        <div className="mb-3 position-relative">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0" style={{ fontWeight: 600 }}>Scanned Item Details</h6>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{ width: '28px', height: '28px' }}
                                                                onClick={handleCloseForm}
                                                                aria-label="Close"
                                                                >
                                                            <X size={16} />
                                                            </button>
                                                        </div>
                                                        <hr className="my-2" />

                                                        <div className="row">
                                                            <div className="col-md-6 mb-2">
                                                            <label className="form-label mb-0"><strong>Name:</strong></label>
                                                            <div className="form-control-plaintext">{selectedScannedItem.name}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                            <label className="form-label mb-0"><strong>Item Group:</strong></label>
                                                            <div className="form-control-plaintext">{selectedScannedItem.item_group}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                            <label className="form-label mb-0"><strong>Item Code:</strong></label>
                                                            <div className="form-control-plaintext">{selectedScannedItem.item_code}</div>
                                                            </div>
                                                            <div className="col-md-6 mb-2">
                                                            <label className="form-label mb-0"><strong>Barcode:</strong></label>
                                                            <div className="form-control-plaintext">{selectedScannedItem.barcode}</div>
                                                            </div>
                                                        </div>
                                                        </div>

                                                        <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center">
                                                            <label htmlFor="custom_quantity" className="form-label mb-0 me-2"><strong>Quantity:</strong></label>
                                                            <input
                                                            type="number"
                                                            id="custom_quantity"
                                                            name="custom_quantity"
                                                            min={1}
                                                            value={customQuantity}
                                                            onChange={(e) => setCustomQuantity(e.target.value)}
                                                            className="form-control form-control-sm"
                                                            style={{ width: '100px' }}
                                                            />
                                                        </div>

                                                        <button type="submit" className="btn btn-dark btn-sm ms-3">
                                                            Save Quantity
                                                        </button>
                                                        </div>
                                                    </form>
                                                    ) : (
                                                    // Label + input section (standard layout)
                                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <span style={{ marginRight: '10px' }}>
                                                        {viewMode === 'scan' ? 'Scan Code:' : 'Upload File:'}
                                                        </span>

                                                        {viewMode === 'scan' ? (
                                                        <input
                                                            ref={scanRef}
                                                            id="scan_code"
                                                            name="scan_code"
                                                            type="text"
                                                            autoComplete="off"
                                                            className="form-control form-control-md w-25"
                                                            style={{ margin: 0 }}
                                                            onKeyDown={(e) => handleScanKeyDown(e, selectedStoreReceiptState.receipt_code)}
                                                        />
                                                        ) : (
                                                        <>
                                                            <input
                                                            ref={fileRef}
                                                            id="file_codes"
                                                            name="file_codes"
                                                            type="file"
                                                            className="form-control form-control-md w-25"
                                                            style={{ margin: 0 }}
                                                            onChange={handleFolderChange}
                                                            />
                                                            <button
                                                            className="btn btn-light"
                                                            style={{ width: "120px", margin: "4px", border: "1px solid #d3d3d3" }}
                                                            onClick={() => handleFileSubmit(selectedStoreReceiptState.receipt_code)}
                                                            disabled={!selectedFile}
                                                            >
                                                            Submit File
                                                            </button>
                                                        </>
                                                        )}
                                                    </div>
                                                    )}
                                                </>
                                                )}
                                          </td>
                                        </tr>
                                    </>
                                }                                  
                            </tbody>
                          </table>
                        </div>

                        {selectedStoreReceiptState && viewMode !== "default" &&
                        <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                            <table className="table table-bordered">
                                <thead>                           
                                    <tr>
                                        <th style={thStyle}>Line No.</th>
                                        <td style={thStyle}>Item Name</td>
                                        <td style={thStyle}>Item Code</td>
                                        <td style={thStyle}>Item Barcode</td>
                                        <td style={thStyle}>Style</td>
                                        <td style={{...thStyle, width: '100px'}}>Qty</td>
                                        {selectedStoreReceiptState?.status === "SCANNING" && (
                                          <th style={thStyle}>Actions</th>
                                        )}
                                    </tr>                                                             
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={selectedStoreReceiptState?.status === "SCANNING" ? 7 : 6} className="text-center">
                                            <div className="spinner-border" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    selectedStoreReceiptLinesState.map((receiptLine: IStoreReceiptLines) =>
                                    receiptLine.lines.map((line: ILine) => {
                                        const isEditing = editingRowId === line.line_num.toString(); // ✅ check per line

                                        return (
                                        <tr key={line.line_num}>
                                            <td style={tdStyle} className="text-center">{line.line_num}</td>
                                            <td style={tdStyle}>{line.item.name}</td>
                                            <td style={tdStyle}>{line.item_code}</td>
                                            <td style={tdStyle}>{line.item.barcode}</td>
                                            <td style={tdStyle}>{line.item.style}</td>
                                            <td style={tdStyle} className="text-end">
                                            {isEditing ? (
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                                    value={inputDraft[line.line_num] ?? ''}
                                                    onChange={(e) => handleQtyDraftChange(line.line_num.toString(), e.target.value)}
                                                    onBlur={() => handleBlur(line.line_num.toString())}
                                                    autoFocus
                                                    onClick={(e) => (e.target as HTMLInputElement).select()}
                                                    onFocus={(e) => (e.target as HTMLInputElement).select()}
                                                />
                                                </div>
                                            ) : (
                                                line.qty
                                            )}
                                            </td>
                                            {selectedStoreReceiptState?.status === "SCANNING" && (
                                            <td style={tdStyle} className="d-flex gap-1">
                                                {isEditing ? (
                                                <>
                                                    {!inputDraft[line.line_num]?.trim() ? (
                                                    <DisabledSaveQtyBtn />
                                                    ) : (
                                                    <button
                                                        className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                        onClick={() => handleSaveClick(line.line_num.toString(), receiptLine.receipt_code, line.item_code )}
                                                        title="Save Quantity"
                                                    >
                                                        <Save style={{ width: "17px", height: "17px" }} />
                                                    </button>
                                                    )}
                                                    <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    onClick={() => setEditingRowId(null)}
                                                    title="Cancel"
                                                    >
                                                    <X style={{ width: "17px", height: "17px" }} />
                                                    </button>
                                                </>
                                                ) : editingRowId === null ? (
                                                <button
                                                    className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                                    onClick={() =>
                                                    handleEditClick(line.line_num.toString(), line.qty ?? 0)
                                                    }
                                                    title="Edit Quantity"
                                                >
                                                    <Pencil style={{ width: "17px", height: "17px" }} />
                                                </button>
                                                ) : (
                                                <DisabledEditQtyBtn />
                                                )}
                                            </td>
                                            )}
                                        </tr>
                                        );
                                    })
                                    )
                                    // selectedStoreReceiptLinesState.map((receiptLine: IStoreReceiptLines) => {

                                    //  const isEditing = editingRowId === receiptLine.lines.line_num;

                                    //  console.log(`What line should we be editing`); 
                                    //  console.log(receiptLine)
                                    //  console.log(`================================`); 

                                    //  console.log(`What line should we be editing`); 
                                    //  console.log(`Editing Row Id `, editingRowId); 
                                    //  console.log(`RecieptLine Id `, receiptLine.id); 
                                    //  console.log(`Is edit state `, isEditing); 
                                    //  console.log(`================================`); 

                                    //  return (
                                    //   // Loop through each receipt and display its lines
                                    //   receiptLine.lines.map((line: ILine) => (
                                    //     <tr key={line.line_num}>
                                    //       <td style={tdStyle} className="text-center">{line.line_num}</td>
                                    //       <td style={tdStyle}>{line.item.name}</td>
                                    //       <td style={tdStyle}>{line.item_code}</td>
                                    //       <td style={tdStyle}>{line.item.barcode}</td>
                                    //       <td style={tdStyle}>{line.item.style}</td>
                                    //       <td style={tdStyle} className="text-end">
                                    //          {isEditing ? (
                                    //             <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    //             <input
                                    //                 type="number"
                                    //                 className="form-control form-control-sm"
                                    //                 style={{ maxWidth: "80px", fontSize: "0.85rem", padding: "0.25rem" }}
                                    //                 value={inputDraft[line.line_num] ?? ''}
                                    //                 onChange={(e) => handleQtyDraftChange(line.line_num.toString(), e.target.value)}
                                    //                 onBlur={() => handleBlur(line.line_num.toString())}
                                    //                 autoFocus
                                    //                 onClick={(e) => (e.target as HTMLInputElement).select()}
                                    //                 onFocus={(e) => (e.target as HTMLInputElement).select()}
                                    //                 />
                                    //                 </div>
                                    //             ) : (
                                    //                 line.qty
                                    //             )}
                                    //       </td>
                                    //         {selectedStoreReceiptState?.status === "SCANNING" && (
                                    //         <td style={tdStyle} className='d-flex gap-1'>
                                    //         {isEditing ? (
                                    //             <>
                                    //               { !inputDraft[line.line_num]?.trim() ? (
                                    //                     <DisabledSaveQtyBtn/>
                                    //                 ) : (
                                    //                     <button
                                    //                     className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                    //                     onClick={() => handleSaveClick(line.line_num.toString())}
                                    //                     title="Save Quantity"
                                    //                     >
                                    //                     <Save style={{ width: "17px", height: "17px" }} />
                                    //                     </button>
                                    //                 )
                                    //              }
                                    //             <button
                                    //                 className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                    //                 onClick={() => setEditingRowId(null)}
                                    //                 title="Cancel"
                                    //             >
                                    //                 <X style={{ width: "17px", height: "17px" }} />
                                    //             </button>
                                    //             </>
                                    //         ) : editingRowId === null ? (
                                    //             <button
                                    //             className="btn btn-secondary h-100 d-flex align-items-center gap-1"
                                    //             onClick={() => {
                                    //                 handleEditClick(
                                    //                 line.line_num.toString(),
                                    //                 line.qty ?? 0
                                    //                 )

                                    //                 console.log(`====== Testing something with qauntity =====`); 
                                    //                 console.log(line.line_num); 
                                    //                 console.log(line.qty);
                                    //                 console.log(`===================`); 

                                    //               }
                                    //             }
                                    //             title="Edit Quantity"
                                    //             >
                                    //             <Pencil style={{ width: "17px", height: "17px" }} />
                                    //             </button>
                                    //         ) : (<DisabledEditQtyBtn/>) }

                                    //         </td>
                                    //         )}
                                    //     </tr>
                                    //   ))
                                    //   );
                                    //   })
                                  )}                                                                                                                                                      
                                </tbody>
                            </table>
                          </div> 
                        }
                    </div> 
                </CardBody>
            </Card>

            { /* Modal setting document to send {start} */ }
             <>
                <Modal
                    show={showUpdateStatusStoreReceiptModal}
                    onHide={() => {
                        setShowUpdateStatusStoreReceiptModal(false);
                        setShouldResetAfterClose(true); // Optional if you want to reset on close
                    }}
                    onExited={() => {
                        if (shouldResetAfterClose) {
                            setSelectedStoreReceiptLinesState([]);
                            setSelectedDocNum("");
                            setSelectedStoreReceiptStateValid(null);
                            setShouldResetAfterClose(false);
                        }
                    }}
                    >
                    <Modal.Header closeButton>
                        <Modal.Title>{`Update Store Receipt Status [${selectedDocNum}]`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                        {(selectedStoreReceiptLinesState.length === 0 ||
                            selectedStoreReceiptLinesState[0].lines.length === 0 ||
                            !selectedStoreReceiptLinesState[0].doc_num) ? (
                            <div className="informational-message-section">
                              <strong>Note:</strong> Unable to close the document. At least one line must be saved to it before submission.
                            </div>
                        ) : (
                            <p>{` Are you sure you would like to send this store reciept`}</p>
                        )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="primary"
                            disabled={
                                selectedStoreReceiptLinesState.length === 0 ||
                                selectedStoreReceiptLinesState[0].lines.length === 0 ||
                                !selectedStoreReceiptLinesState[0].doc_num
                            }
                            onClick={() => {
                                handleRecieptCloseSubmit(); // or your store receipt submit handler
                                setShowUpdateStatusStoreReceiptModal(false);
                                setShouldResetAfterClose(true);
                            }}
                         >
                          Confirm
                        </Button>
                        <Button variant="secondary"
                                onClick={() => {
                                    setShowUpdateStatusStoreReceiptModal(false);
                                    setShouldResetAfterClose(true);
                         }}
                        >
                        Cancel
                        </Button>
                    </Modal.Footer>
                </Modal>
             </>
             
            { /* Modal setting document to send {end} */ }


               {/* Modal Delete Store Receipt - Start */}
                <>
                    <Modal show={showDeleteStoreReceiptModal} onHide={() => { setShowDeleteStoreReceiptModal(false); }}>
                        <Modal.Header closeButton>
                            <Modal.Title>{`Delete Store Receipt [${selectedDocNum}]`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to delete this store receipt?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowDeleteStoreReceiptModal(false); handleRecieptDeleteSubmit();  }}>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={() => { setShowDeleteStoreReceiptModal(false); } }>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                </> 
                {/* Modal Modal Delete Store Receipt - End */}

        </>
    );
} 

const ReceiveStock: React.FC<IComponent | null> = (props) => {
    
    const {fetchData, userDetail}  = useAuth();
    const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
    const queryUrlSelectNoCount: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
    const [selectedStockMovementOut, setSelectedStockMovementOut] = useState<IStockMovementOut | null>(null);
    const [receiveStockMovementOut, setReceiveStockMovementOut] = useState<IStockMovementOut | null>(null);
    const [stockMovementState, setStockMovementState] = useState<IStockMovementOut[]>([]);
    const [stockMovementLineState, setStockMovementLineState] = useState<IStockMovementOutLine[]>([]);
    const [totalQtyOrderedState, setTotalQtyOrderedState] = useState<number>(0)
    const [totalQtySentState, setTotalQtySentState] = useState<number>(0)
    const [totalQtyReceivedState, setTotalQtyReceivedState] = useState<number>(0)
    const [hasPostedReciepts, setHasPostedReciepts] = useState<boolean>(false);

    const [submitRefreshData, setSubmitRefreshData] = useState<boolean>(false); 
    const [submitRefreshFailedTransactionData, setSubmitRefreshFailedTransactionData] = useState<boolean>(false);
    const [stockMovementFailedState, setStockMovementFailedState] = useState<IStockMovementOut[]>([]);
    const [selectedFailedStockMovementOut, setSelectedFailedStockMovementOut] = useState<IStockMovementOut | null>(null);
    const [showFailedStockMovementOutRerunModal, setShowFailedStockMovementOutRerunModal] = useState<boolean>(false);
    const [showFailedStockMovementOutHandledModal, setShowFailedStockMovementOutHandledModal] = useState<boolean>(false);
    const [expanded, setExpanded] = useState(false);

    // // -> failed transactions
    const defaultQueryStateFailedTransactions: IQuerySelectData = {
            appCode: "ares",
            selectCode: "sa-m24l-stock-movement-out",  
            where: `to_warehouse = :to_warehouse AND handled_status IS NOT DISTINCT FROM :handled_status`,  
            orderBy: "",
            params: {'to_warehouse': `${props?.store.code}`, 'handled_status': "REQUIRED"}
    }; 
    const [querySelectStateFailed, setQuerySelectStateFailed] = useState<IQuerySelectData>(defaultQueryStateFailedTransactions);

    // // -> successful transactions
    const defaultQueryStateSuccessfulTransactions: IQuerySelectData = {
            appCode: "ares",
            selectCode: "sa-m24l-stock-movement-out",  
            where: `to_warehouse = :to_warehouse`,  
            orderBy: "",
            params: {'to_warehouse': `${props?.store.code}`}
    }; 
    const [querySelectStateSuccessful, setQuerySelectStateSuccessful] = useState<IQuerySelectData>(defaultQueryStateSuccessfulTransactions);

    const [queryLineState, setQueryLineState] = useState<IQuery>({
        tableName: "sa_m24l_stock_movement_out_line",
        select: [],
        groupBy: [],
        join: [
            {
                relationship: "item",
                joinType: "LEFT"
              }            
        ],
        orderBy: [ 
            {
                fullColumnName: "line_num",
                direction: "ASC",
                cast: CastType.INTEGER
            }
        ],
        where:[
            {
                fullColumnName: "doc_num",
                value: selectedStockMovementOut?.doc_num,
                operator: "EQ",
                cast: CastType.INTEGER
            }
        ],
        mainTableOnly: false,
        includeExtraColumns: true,
        includeControlColumns: true,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        // pageNum: 0,
        // pageSize: 100,
        frontEndOnly: null
    });

    useEffect(() => {
        fetchData(queryUrlSelectNoCount, {method:"POST", body: JSON.stringify(querySelectStateSuccessful)}, processDataSuccessful);
    }, [hasPostedReciepts, querySelectStateSuccessful, submitRefreshData]);

    useEffect(() => {
        if(expanded){
           fetchData(queryUrlSelectNoCount, {method:"POST", body: JSON.stringify(querySelectStateFailed)}, processDataFailed);
        }
    }, [querySelectStateFailed, expanded, submitRefreshFailedTransactionData])


    useEffect(() => {
        const queryLine = {...queryLineState}
        queryLine.where = [
            {
                fullColumnName: "doc_num",
                value: selectedStockMovementOut?.doc_num,
                operator: "EQ",
                cast: CastType.INTEGER
            }           
        ]
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryLine)}, processLineData);
    }, [selectedStockMovementOut]);

    const processDataSuccessful = (result: any) => {
        const stockMovements: IStockMovementOut[] = [];
        
        for(const key in result.data){
            stockMovements.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
        }

        // Sorting by doc_num in descending order
        stockMovements.sort((a, b) => b.doc_num - a.doc_num);

        setStockMovementState(stockMovements);
    }

    const processDataFailed = (result: any) => {
        const stockMovements: IStockMovementOut[] = [];
        
        for(const key in result.data){
            stockMovements.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
        }

        // Sorting by doc_num in descending order
        stockMovements.sort((a, b) => b.doc_num - a.doc_num);

        setStockMovementFailedState(stockMovements);
    }

    const processLineData = (result: any) => {
        
        const stockMovementLines: IStockMovementOutLine[] = [];

        //console.log(result);
        
        let totalQtyOrdered: number = 0;
        let totalQtySent: number = 0;
        let totalQtyReceived: number = 0;

        for(const key in result.data){
            
            totalQtyOrdered += result.data[key].data.qty_ordered;
            totalQtySent += result.data[key].data.qty_sent;
            totalQtyReceived += result.data[key].data.qty_received;

            stockMovementLines.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
            //console.log({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
        }

         // Sort the stockMovementLines array: null line_num first
         stockMovementLines.sort((a, b) => {
            if (a.line_num === null) return -1; // a comes first if line_num is null
            if (b.line_num === null) return 1;  // b comes first if line_num is null
            return a.line_num - b.line_num;      // otherwise, sort numerically
        }); 

        setTotalQtyOrderedState(totalQtyOrdered);
        setTotalQtySentState(totalQtySent);
        setTotalQtyReceivedState(totalQtyReceived);
        setStockMovementLineState(stockMovementLines);
    }


    const handleReceiveStatusClick = (stockMovementOut: IStockMovementOut) => {
        setSelectedStockMovementOut(stockMovementOut);

        const query = { ...querySelectStateSuccessful }; // or Failed, depending on which you're updating
        query.where = `${defaultQueryStateSuccessfulTransactions.where} AND id = :id`;
        query.params = {
            ...defaultQueryStateSuccessfulTransactions.params,
            id: stockMovementOut.id
        };

        setQuerySelectStateSuccessful(query);
    };

    const handleReceiveClick = (stockMovementOut: IStockMovementOut) => {
        //console.log(`RECEIVE CLICK ${JSON.stringify(stockMovementOut)}`);
        setReceiveStockMovementOut(stockMovementOut);
    }


    const handleResetClick = () => {
        setSelectedStockMovementOut(null);
        setQuerySelectStateSuccessful(defaultQueryStateSuccessfulTransactions);
    };

   
    const DisabledStatusBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Status"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
         <FileText style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
       )
    } 

    const toggleExpanded = () => setExpanded((prev) => !prev);


    //handle the failed transaction click rerun
    const handleFailedStockMovementOutRerunClick = (stockMovementOut : IStockMovementOut) => {
        setSelectedFailedStockMovementOut(stockMovementOut);
        setShowFailedStockMovementOutRerunModal(true);
    }

    const handleFailedStockMovementOutRerunResetClick = () => {
        setSelectedFailedStockMovementOut(null);
        setShowFailedStockMovementOutRerunModal(false);
    }
    
    const handleFailedStockMovementOutRerunSubmit = () => {
         if(selectedFailedStockMovementOut?.doc_num != null && selectedFailedStockMovementOut?.function_call_id != null){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedFailedStockMovementOut.function_call_id}/rerun`,
            { method: "POST", body: JSON.stringify({}) },
            (result: any, status: string) => {
                if (status === "200") {
                    setSelectedFailedStockMovementOut(null);
                    setSubmitRefreshFailedTransactionData(prev => !prev); // trigger refresh on success
                } else {
                    setSelectedFailedStockMovementOut(null);
                }
            }
        );
        }else {
          return; 
        }
    }
    
    //handle the failed transaction click handled
    const handleFailedStockMovementOutHandledClick = (stockMovementOut : IStockMovementOut) => {
        setSelectedFailedStockMovementOut(stockMovementOut); 
        setShowFailedStockMovementOutHandledModal(true);
    }

    const handleFailedStockMovementOutHandledResetClick = () => {
        setSelectedFailedStockMovementOut(null);
        setShowFailedStockMovementOutHandledModal(false);
    }
    
    
    const handleFailedStockMovementOutHandledSubmit = () => {
        if(selectedFailedStockMovementOut?.doc_num != null && selectedFailedStockMovementOut?.function_call_id != null){
            fetchData(`${process.env.REACT_APP_BASE_URL}/functionCalls/${selectedFailedStockMovementOut.function_call_id}/handle`,
            { method: "POST", body: JSON.stringify({}) },
            (result: any, status: string) => {
                if (status === "200") {
                    setSelectedFailedStockMovementOut(null);
                    setSubmitRefreshFailedTransactionData(prev => !prev); // trigger refresh on success
                } else {
                    setSelectedFailedStockMovementOut(null);
                }
            }
        );
        }else {
          return; 
        }
    }
      
  
    const thStyle = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const thNewStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        borderTop: "1px solid #dee6ed",
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

    const tdStyle = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };

    return (
        <>
            {receiveStockMovementOut ? ( <StoreReceipt setHasPostedReciepts={setHasPostedReciepts} hasPostedReciepts={hasPostedReciepts} stockMovmentOut={receiveStockMovementOut} close={() => {setReceiveStockMovementOut(null)}} /> ) :
            (
        
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title className="mb-0">{`Receive Stock - [${props?.store.name}]`}</Card.Title>
                <button
                    className="btn p-1 d-flex align-items-center justify-content-center"
                    onClick={toggleExpanded}
                    title={expanded ? "Hide failed transactions" : "Show failed transactions"}
                    style={{ border: "1px solid #c10000", background: "transparent", borderRadius: "50%", color: "#c10000", width: "22px", height: "22px"}}
                >
                    {expanded ? (
                    <ChevronUp size={20} strokeWidth={2.2} />
                    ) : (
                    <ChevronDown size={20} strokeWidth={2.2} />
                    )}
                </button>
                </Card.Header>
                <CardBody>                   
                    <div>
                        <button
                            className="btn btn-secondary"
                            style={{width: "120px"}}
                            onClick={() => props?.close()}
                        >
                            Back to Stores
                        </button>
                    </div>
                    {expanded && stockMovementFailedState.length > 0 && (
                    <div style={{border: "1px solid #c10000" , padding: '0px 4px', marginBottom: '14px', marginTop: '14px', borderRadius: '5px'}}>
                        <h5 className="mt-2 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                            Failed Transactions: 
                        </h5>
                    
                        <div
                        style={{
                        maxHeight: stockMovementFailedState.length > 10 ? "450px" : "unset", // change based on count
                        overflowY: stockMovementFailedState.length > 10 ? "auto" : "visible",
                        marginBottom: "4px",
                        position: "relative",
                        backgroundColor: "#fff", // helps hide any pixel bleed
                        borderBottom: "1px solid #dee6ed",
                        }}
                    >
                    <table className="table table-bordered  mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                        <thead
                        className="table-light"
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            backgroundColor: "#f8f9fa",
                            boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.05)",
                        }}
                        >
                        <tr>
                            <th style={{ ...thNewStyle, width: "160px"}}>Doc Num</th>
                            <th style={thNewStyle}>Message</th>
                            <th style={thNewStyle}>Last Run</th>
                            <th style={{ ...thNewStyle, width: "100px"}}>
                            Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                         { stockMovementFailedState.length > 0 && stockMovementFailedState.map((stockMovementOut: IStockMovementOut, index) => (
                            <tr key={index}>
                            <td style={tdStyle}>{stockMovementOut.doc_num}</td>
                            <td style={{...tdStyle,
                                maxWidth: "300px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                               }}>
                               {stockMovementOut.extended_message}
                            </td>
                            <td style={tdStyle}>{formatDateFull(stockMovementOut.completed_at ?? "")}</td>
                            <td style={{...tdStyle}} className="d-flex gap-1">
                                { /* rerun function */ }
                                <button className="btn btn-secondary d-flex align-items-center gap-1" title="Rerun"
                                  onClick={() => handleFailedStockMovementOutRerunClick(stockMovementOut)}
                                >
                                <RotateCcw  style={{ width: "17px", height: "17px" }}/>
                                </button>
                                { /* handled function */ }
                                <button className="btn btn-secondary d-flex align-items-center gap-1" title="Handled"
                                onClick={() => handleFailedStockMovementOutHandledClick(stockMovementOut)}
                                >
                                <CheckCircle style={{ width: "17px", height: "17px" }} />
                                </button>
                            </td>
                          </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    <div style={{marginBottom: "3px", marginLeft: "10px", color: "#c10000" }}>
                        <small className="text-muted fw-bolder">Total Failed Transactions: {stockMovementFailedState.length}</small>
                    </div>
                    
                   </div> 
                  )}
                    <br></br>
                    <div>
                       <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                         <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Doc No.</th>
                                    <th style={thStyle}>Card Code</th>
                                    <th style={thStyle}>From Warehouse</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Updated</th>
                                    <th style={{...thStyle, width: "100px"}}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                            {stockMovementState.map((stockMovement: IStockMovementOut) => (
                                <tr key={stockMovement.id}>
                                    <td style={tdStyle} className="text-center"><b>{stockMovement.doc_num}</b></td>
                                    <td style={tdStyle}>{stockMovement.card_code}</td>
                                    <td style={tdStyle}>{stockMovement.from_warehouse}</td>
                                    <td style={tdStyle} className={`${stockMovement.integration_status === 'DISPATCHED' ? 'bg-warning text-white text-center' 
                                        : (stockMovement.integration_status === 'CLOSED' || stockMovement.integration_status === 'TO_CLOSE') ? 'bg-success text-white text-center' 
                                        : ''}`}>
                                        {stockMovement.integration_status}
                                    </td>
                                    <td style={tdStyle}>{formatDateFull(stockMovement.created)}</td>
                                    <td style={tdStyle}>{formatDateFull(stockMovement.updated)}</td> 
                                    <td style={tdStyle} className="d-flex gap-1">
                                        {selectedStockMovementOut === null ? (
                                            <>
                                               {stockMovement.integration_status !== 'DISPATCHED' ? 
                                                ( 
                                                 <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Status" onClick={() => handleReceiveStatusClick(stockMovement)}>
                                                    <FileText style={{ width: "17px", height: "17px" }} />
                                                 </button>
                                                ) 
                                                : 
                                                (
                                                    <DisabledStatusBtn/>
                                                )
                                               }
                                               <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Receive" onClick={() => handleReceiveClick(stockMovement)}>
                                                   <PackageCheck style={{ width: "17px", height: "17px" }} />
                                               </button>    
                                        </>                                       
                                        ) : (
                                            <button
                                                className="btn btn-secondary"
                                                style={{width: "120px"}}
                                                onClick={() => handleResetClick()}
                                            >
                                                Back to Store
                                            </button>
                                        )}                                                                  
                                    </td>
                                </tr>
                            ))}                                                                                                                                                         
                            </tbody>
                         </table>
                        </div>

                        {selectedStockMovementOut &&
                        <div style={{ overflowY: "auto" , marginBottom: "4px", position: "relative", backgroundColor: "#fff", borderBottom: "1px solid #dee6ed" }}>
                            <table className="table table-bordered">
                            <thead>                           
                                <tr>
                                    <th style={thStyle}>Line No.</th>
                                    <th style={thStyle}>Item Code</th>
                                    <th style={thStyle}>Style</th>
                                    <th style={thStyle}>Qty Ordered</th>
                                    <th style={thStyle}>Qty Sent</th>
                                    <th style={thStyle}>Qty Received</th>
                                    {/* <td><b>Action</b></td> */}
                                </tr>
                                <tr>
                                    <td style={tdStyle} colSpan={3}></td>
                                    <td style={tdStyle} className="text-center"><b>{totalQtyOrderedState}</b></td>
                                    <td style={tdStyle} className="text-center"><b>{totalQtySentState}</b></td>
                                    <td style={tdStyle} className="text-center"><b>{totalQtyReceivedState}</b></td>
                                    {/* <td><b>Action</b></td> */}
                                </tr>                            
                            </thead>
                            <tbody>
                            {stockMovementLineState.map((stockMovementLine: IStockMovementOutLine) => (
                                    <tr key={stockMovementLine.id}>
                                        <td style={tdStyle} className="text-center">{stockMovementLine.line_num === null ? "-" : stockMovementLine.line_num}</td>
                                        <td style={tdStyle}>{stockMovementLine.item_code}</td>
                                        <td style={tdStyle}>{stockMovementLine["item.style"]}</td>
                                        <td style={tdStyle} className={`${stockMovementLine.line_num === null ? 'bg-danger font-bolding text-white text-center' : stockMovementLine.line_num !== null && (stockMovementLine.qty_sent !== stockMovementLine.qty_received || stockMovementLine.qty_sent !== stockMovementLine.qty_ordered || stockMovementLine.qty_sent !== stockMovementLine.qty_received) ? 'bg-warning font-bolding text-white text-center' : 'bg-success font-bolding text-white text-center'}`}>
                                            {stockMovementLine.qty_ordered === null ? "-" : stockMovementLine.qty_ordered}
                                        </td>
                                        <td style={tdStyle} className={`${stockMovementLine.line_num === null ? 'bg-danger font-bolding text-white text-center' : stockMovementLine.line_num !== null && (stockMovementLine.qty_sent !== stockMovementLine.qty_received || stockMovementLine.qty_sent !== stockMovementLine.qty_ordered || stockMovementLine.qty_sent !== stockMovementLine.qty_received) ? 'bg-warning font-bolding text-white text-center' : 'bg-success font-bolding text-white text-center'}`}>
                                           {stockMovementLine.qty_sent === null ? "-" : stockMovementLine.qty_sent}
                                        </td>
                                        <td style={tdStyle} className={`${stockMovementLine.line_num === null ? 'bg-danger font-bolding text-white text-center' : stockMovementLine.line_num !== null && (stockMovementLine.qty_sent !== stockMovementLine.qty_received || stockMovementLine.qty_sent !== stockMovementLine.qty_ordered || stockMovementLine.qty_sent !== stockMovementLine.qty_received) ? 'bg-warning font-bolding text-white text-center' : 'bg-success font-bolding text-white text-center'}`}>
                                            {stockMovementLine.qty_received === null ? "-" : stockMovementLine.qty_received}
                                        </td>
                                    </tr>
                                ))}                                                                                                                                                         
                            </tbody>
                          </table>
                        </div>
                        }

                    </div>
                </CardBody>

                {/* Modal Failed Transaction Rerun - Start */}
                <>
                    <Modal show={showFailedStockMovementOutRerunModal} onHide={handleFailedStockMovementOutRerunResetClick}>
                        <Modal.Header closeButton>
                            <Modal.Title>{`Rerun Failed [${selectedFailedStockMovementOut?.doc_num}] Transaction`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to rerun this transaction?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowFailedStockMovementOutRerunModal(false); handleFailedStockMovementOutRerunSubmit(); } }>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={handleFailedStockMovementOutRerunResetClick}>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                </> 
                {/*  Modal Failed Transaction Rerun - End */}


                {/* Modal Failed Transaction Handled - Start */}
                <>
                    <Modal show={showFailedStockMovementOutHandledModal} onHide={handleFailedStockMovementOutHandledResetClick}>
                        <Modal.Header closeButton>
                            <Modal.Title>{`Handle Failed [${selectedFailedStockMovementOut?.doc_num}] Transaction`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                            <p>Are you sure you would like to mark this transaction as handled?</p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { setShowFailedStockMovementOutHandledModal(false); handleFailedStockMovementOutHandledSubmit(); } }>
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={handleFailedStockMovementOutHandledResetClick}>
                            Cancel
                        </Button>
                        </Modal.Footer>
                    </Modal>
                 </> 
                {/*  Modal Failed Transaction Handled - End */}


            </Card>                    
            )}
        </>

    );
}

const View: React.FC<IComponent | null> = (props) => {


     const cellStyle: React.CSSProperties = {
       border: '1px solid #deebed',
       whiteSpace: 'nowrap',
       padding: '5px 8px',
       fontSize: '0.85rem',
       verticalAlign: 'middle',
       fontWeight: 500,
    };

    const cellLabelStyle: React.CSSProperties = {
       backgroundColor: '#f7f5f5',
       border: '1px solid #deebed',
       whiteSpace: 'nowrap',
       padding: '10px 8px',
       fontSize: '0.85rem',
       verticalAlign: 'middle',
       fontWeight: 600,
    };

    return (
        <>
        <Card>
            <CardBody>    
                 <div>
                    <button className="btn btn-secondary" style={{ width: "120px" }} onClick={() => props?.close()}>
                    Back to Stores
                    </button>
                </div>
                <br/>
                <div >
                    {/* Store Details Header */}
                    <h5 className="mt-2 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                        Store Details
                    </h5>
                    <table className="notification-summary-table"
                    style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: '#fff',  border: '1px solid #deebed'}}
                    >
                    <colgroup>
                        <col style={{ width: '200px' }} />
                        <col />
                    </colgroup>
                    <tbody>
                        <tr>
                        <td style={cellLabelStyle}>Code:</td>
                        <td style={cellStyle}>{props?.store.code}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>Name:</td>
                        <td style={cellStyle}>{props?.store.name}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>Email:</td>
                        <td style={cellStyle}>{props?.store.email}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>Customer Code:</td>
                        <td style={cellStyle}>{props?.store.customer_code}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>Costing Code:</td>
                        <td style={cellStyle}>{props?.store.costing_code}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>SIT Location:</td>
                        <td style={cellStyle}>{props?.store.sit_location}</td>
                        </tr>
                        <tr>
                        <td style={cellLabelStyle}>Shopify Id:</td>
                        <td style={cellStyle}>{props?.store.shopify_location_id}</td>
                        </tr>
                    </tbody>
                    </table>
                </div>
            </CardBody>
        </Card>  
      
        </>

    );
}

const StoreList: React.FC<{handleComponentDisplay: (store: IStore, component: React.FC<IComponent | null>, isCycleCount?: boolean, allStores?: IStore[]) => void}> = (props) => {
    
    const { fetchData, userDetail}  = useAuth();
    const [storesState, setStoresState] = useState<IStore[]>([]);
    const queryUrlSelect: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data?includeCount=true"}`;
    const [activeInfo, setActiveInfo] = useState<string | null>(null);

    const defaultQueryState: IQuerySelectData = {
          appCode: "ares",
          selectCode: "store-summary",  
          where: "",  
          orderBy: "",
    };    
    
    const [queryState, setQueryState] = useState<IQuerySelectData>(defaultQueryState);
    
    useEffect(() => {
        fetchStores();
    }, [userDetail]);

    const processStores = (result: any) => {

        const stores: IStore[] = [];
        for(const key in result.data){
            stores.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined, ...{ "id" :result.data[key].id } });
        }

        // Sort stores alphabetically by name
        stores.sort((a, b) => {
            const nameA = a.name?.toLowerCase() || '';
            const nameB = b.name?.toLowerCase() || '';
            return nameA.localeCompare(nameB);
        });


        setStoresState(stores);
    }

    const fetchStores = () => {
        fetchData(queryUrlSelect, {method:"POST", body: JSON.stringify(queryState)}, processStores);
    }


   const getButtonVariantByDate = (dateStr?: string | null, warningHours = 24, dangerHours = 48): string => {
        if (!dateStr) return 'btn-secondary';

        const createdDate = new Date(dateStr);
        const now = new Date();
        const diffHours = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);

        if (diffHours < dangerHours) return 'btn-success';
        if (diffHours > dangerHours) return 'btn-danger';
        if (diffHours > warningHours) return 'btn-warning';
        return 'btn-secondary';
    };


     const getElapsedTime = (dateStr?: string | null, warningHours = 24, dangerHours = 48): string => {
         if (!dateStr) return 'No Open Activity';

        const createdDate = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - createdDate.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);
        const diffDays = diffHours / 24;

        if (diffDays > 365) {
            return 'Current Activity';
        }

        if (diffHours > dangerHours) {
            const daysElapsed = Math.floor(diffDays);
            return `Current Activity`;
        }

        if (diffHours > warningHours) {
            return 'Current Activity';
        }

        return 'Current Activity';
     };

    const getCountButton = ( label: string, count: number | undefined, date: string | null | undefined, onClick: () => void ) => (
        <button className={`btn ${getButtonVariantByDate(date)} d-flex align-items-center justify-content-between px-2 py-1`} 
                style={{ fontSize: '0.75rem', minWidth: '190px', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)', transition: 'box-shadow 0.2s ease-in-out' }} onClick={onClick} title={label}>
            <span className="text-truncate" style={{ maxWidth: '160px' }}>
                {getElapsedTime(date)}
            </span>
            <span className="bg-light text-black rounded d-inline-flex align-items-center justify-content-center" style={{ minWidth: '20px', height: '20px', padding: '0 4px', fontSize: '0.75rem', lineHeight: 1 }}>
            {count}
            </span>
        </button>
    );



    const handleInfoClick = (key: string) => {
      setActiveInfo(prev => (prev === key ? null : key)); // toggle
    };

    const infoMap: Record<string, string> = {
        storeReceipts: "Manages the receipting of incoming stock from the warehouse to stores.",
        stockTakes: "A stock take is a physical inventory count to verify accuracy against system records. It updates the system Stock on Hand as per the scanned inventory.",
        cycleCounts: "A cycle count is a targeted inventory check of select items, verifying stock accuracy without a full shutdown. Cycle Count only impact the scanned items.",
        storeReturns: "Manages the process of returning stock from stores back to the warehouse.",
        stockTransfers: "Manages inventory transfers between departments (internal) and stores (external send/receive).",
    };

    const InfoBubble = ({ text }: { text: string }) => (
        <div style={{ position: 'absolute', top: '1.8rem', right: '0.2rem', zIndex: 10, background: 'white', border: '1px solid #ccc', borderRadius: '8px', padding: '3px 3px', width: '210px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: '0.85rem'}} >
            {text}
        </div>
    );


    const thStyle = {
      backgroundColor: '#f7f5f5',
      border: '1px solid #deebed',
      fontWeight: 600,
      padding: '10px 8px',
      fontSize: '0.85rem',
    };

    const cellStyle: React.CSSProperties = {
       border: '1px solid #deebed',
       whiteSpace: 'nowrap',
       padding: '5px 8px',
       fontSize: '0.85rem',
       verticalAlign: 'middle',
       fontWeight: 500,
    };

    const cellLabelStyle: React.CSSProperties = {
       backgroundColor: '#f7f5f5',
       border: '1px solid #deebed',
       whiteSpace: 'nowrap',
       padding: '10px 8px',
       fontSize: '0.85rem',
       verticalAlign: 'middle',
       fontWeight: 600,
    };

    const getEarliestDate = (dateA?: string | null, dateB?: string | null): string | null => {
        if (!dateA) return dateB || null;
        if (!dateB) return dateA || null;

        return new Date(dateA) < new Date(dateB) ? dateA : dateB;
    };

    const TableComponent = (storesState: IStore[]) => {
        const colGroup = (
            <colgroup>
            <col style={{ width: '180px' }} />
            <col style={{ width: '190px' }} />
            <col style={{ width: '190px' }} />
            <col style={{ width: '190px' }} />
            <col style={{ width: '190px' }} />
            <col style={{ width: '190px' }} />
            </colgroup>
        );

        const totals = storesState.reduce(
            (acc, store) => {

            const dispatched = store.dispatched_count || 0;
            const receipts = store.stock_receipt_count || 0;

            acc.receipts += receipts + dispatched; 
            acc.dispatchedTotal += store.dispatched_count || 0;
            acc.receipts += store.stock_receipt_count || 0;
            acc.takes += store.stock_take_count || 0;
            acc.cycles += store.cycle_count_count || 0;
            acc.returns += store.stock_return_count || 0;
            acc.transfers += store.stock_transfer_count || 0;
            return acc;
            },
            { dispatchedTotal: 0, receipts: 0, takes: 0, cycles: 0, returns: 0, transfers: 0 }
        );

        return (
            <div style={{ width: '100%' }}>
            {/* First Table - Total Counts */}
             <h5 className="mt-3 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                Store Overview
            </h5>
            <table
                className="notification-summary-table fixed-table"
                style={{ width: '100%', tableLayout: 'fixed' }}
            >
                {colGroup}
                <thead>
                <tr>
                    <th style={thStyle}></th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Store Receipts</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Stock Takes</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Cylce Counts</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Store Returns (Warehouse)</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Stock Transfers (IBT)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td style={{ ...cellLabelStyle, textAlign: 'center' }}>
                      Total Open Transactions
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600'}}>{totals.receipts}</td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600'}}>{totals.takes}</td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600'}}>{totals.cycles}</td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600'}}>{totals.returns}</td>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: '600'}}>{totals.transfers}</td>
                </tr>
                </tbody>
            </table>

              {/* Store Management Header */}
             <h5 className="mt-5 mb-2 text-uppercase text-secondary fw-bolder border-bottom pb-1">
                Store Management
            </h5>


            {/* Second Table - Per Store Details */}
            <table
                className="notification-summary-table fixed-table"
                style={{ width: '100%', tableLayout: 'fixed' }}
            >
                {colGroup}
                <thead>
                <tr>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Store Name</th>
                    <th style={{ position: 'relative', paddingRight: '1.5rem', textAlign: 'center', ...thStyle }}>
                        Store Receipts
                        <span style={{ position: 'absolute', top: '-2px', right: '0px', cursor: 'pointer'}} onClick={() => handleInfoClick('storeReceipts')}>
                            <MessageSquareText size={14} color="#6c757d" />
                        </span>
                         {activeInfo === 'storeReceipts' && <InfoBubble text={infoMap['storeReceipts']} />}
                    </th>
                     <th style={{ position: 'relative', paddingRight: '1.5rem', textAlign: 'center', ...thStyle }}>
                        Stock Takes
                        <span style={{ position: 'absolute', top: '-2px', right: '0px', cursor: 'pointer'}} onClick={() => handleInfoClick('stockTakes')}>
                            <MessageSquareText size={14} color="#6c757d" />
                        </span>
                        {activeInfo === 'stockTakes' && <InfoBubble text={infoMap['stockTakes']} />}
                    </th>
                    <th style={{ position: 'relative', paddingRight: '1.5rem', textAlign: 'center', ...thStyle }}>
                        Cylce Counts
                        <span style={{ position: 'absolute', top: '-2px', right: '0px', cursor: 'pointer'}} onClick={() => handleInfoClick('cycleCounts')}>
                            <MessageSquareText size={14} color="#6c757d" />
                        </span>
                        {activeInfo === 'cycleCounts' && <InfoBubble text={infoMap['cycleCounts']} />}
                    </th>
                    <th style={{ position: 'relative', paddingRight: '1.5rem', textAlign: 'center', ...thStyle }}>
                        Store Returns (Warehouse)
                        <span style={{ position: 'absolute', top: '-2px', right: '0px', cursor: 'pointer'}} onClick={() => handleInfoClick('storeReturns')}>
                            <MessageSquareText size={14} color="#6c757d" />
                        </span>
                        {activeInfo === 'storeReturns' && <InfoBubble text={infoMap['storeReturns']} />}
                    </th>
                    <th style={{ position: 'relative', paddingRight: '1.5rem', textAlign: 'center', ...thStyle }}>
                        Stock Transfers (IBT)
                        <span style={{ position: 'absolute', top: '-2px', right: '0px', cursor: 'pointer'}} onClick={() => handleInfoClick('stockTransfers')}>
                            <MessageSquareText size={14} color="#6c757d" />
                        </span>
                        {activeInfo === 'stockTransfers' && <InfoBubble text={infoMap['stockTransfers']} />}
                    </th>
                </tr>
                </thead>
                <tbody>
                {storesState.map((store: IStore) => (
                    <tr key={store.code}>
                    <td style={{ ...cellStyle, paddingRight: '4px' }}>
                        <span
                            style={{
                            lineHeight: '1',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            display: 'inline-block',
                            maxWidth: '100%',         // Ensure it doesn’t exceed column width
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            }}
                            title={store.name}
                            onClick={() => props.handleComponentDisplay(store, ComponentEnum.View)}
                        >
                            {store.name}
                        </span>
                    </td>
                    <td style={cellStyle}>
                        <div className="d-flex justify-content-center">
                            {getCountButton(
                            'View Store Receipt Details',
                            (store?.stock_receipt_count || 0) + (store?.dispatched_count || 0),
                            getEarliestDate(store?.stock_receipt_min_created, store?.dispatched_min_created),
                            () =>
                                props.handleComponentDisplay(
                                store,
                                ComponentEnum.ReceiveStock
                                )
                            )}
                        </div>
                    </td>
                    <td style={cellStyle}>
                        <div className="d-flex justify-content-center">
                        {getCountButton(
                            'View Stock Takes Details',
                            store?.stock_take_count,
                            store?.stock_take_min_created,
                            () =>
                            props.handleComponentDisplay(
                                store,
                                ComponentEnum.StockTake,
                                false
                            )
                        )}
                        </div>
                    </td>
                    <td style={cellStyle}>
                        <div className="d-flex justify-content-center">
                        {getCountButton(
                            'View Cycle Count Details',
                            store?.cycle_count_count,
                            store?.cycle_count_min_created,
                            () =>
                            props.handleComponentDisplay(
                                store,
                                ComponentEnum.StockTake,
                                true
                            )
                        )}
                        </div>
                    </td>
                    <td style={cellStyle}>
                        <div className="d-flex justify-content-center">
                        {getCountButton(
                            'View Stock Return Details',
                            store?.stock_return_count,
                            store?.stock_return_min_created,
                            () =>
                            props.handleComponentDisplay(
                                store,
                                ComponentEnum.StoreReturn
                            )
                        )}
                        </div>
                    </td>
                     <td style={cellStyle}>
                        <div className="d-flex justify-content-center">
                            {getCountButton(
                            'View Stock Transfer Details',
                            store?.stock_transfer_count,
                            store?.stock_transfer_min_created,
                            () => {
                                console.log('Store clicked:', store);
                                props.handleComponentDisplay(
                                store,
                                ComponentEnum.IBTManagement,
                                undefined,
                                storesState
                                );
                            }
                            )}
                        </div>
                    </td>
                </tr>
                ))}
                </tbody>
            </table>
          </div>
          );
        };



    return (
        <Card>
            <CardBody>    
                <div className="notification-summary-outer-container"  style={{ maxHeight: 'none' }} >
                    <div className="outer-inner-container">
                        {TableComponent(storesState)}
                    </div>
                </div>
            </CardBody>
        </Card>              
    )
}

const ComponentEnum = {
    IBTManagement: IBTManagement,
    StoreReturn: StoreReturn,
    StockTake: StockTake,
    ReceiveStock: ReceiveStock,
    View: View
}

type ComponentEnumKeys = keyof typeof ComponentEnum;

export const StoreManagement: React.FC = () => {

    const [VisibleComponent, setVisibleComponent] = useState<React.FC<IComponent | null> | null>(null);
    const [currentIsCycleCount, setCurrentIsCycleCount] = useState<boolean | undefined>(undefined);
    const [CurrentStore, setCurrentStore] = useState<IStore>(
        {
            id: "",
            created: "",
            updated: null,
            code: "",
            name: "",
            email: "",
            customer_code: "",
            costing_code: "",
            sit_location: "",
            shopify_location_id: ""
        }        
    );
    const [allStores, setAllStores] = useState<IStore[]>([]);

   


    const handleComponentDisplay = (store: IStore, component: React.FC<IComponent | null>, isCycleCount?: boolean, allStores?: IStore[]) => {
        setCurrentIsCycleCount(isCycleCount);
        setCurrentStore(store);
        setAllStores(allStores ?? []);
        if(component === null){
            setVisibleComponent(null);
        } else {
           setVisibleComponent(() => component);
        }
    }


    return(
        <React.Fragment>
            <Container fluid className="p-0">
                {VisibleComponent !== null && <VisibleComponent store={CurrentStore} close={() => setVisibleComponent(null)} isCycleCount={currentIsCycleCount} allStores={allStores} />}
                {VisibleComponent === null && <StoreList handleComponentDisplay={handleComponentDisplay} />}
            </Container>
        </React.Fragment>
    );
}